'use client';

import { forwardRef } from '@ironhack/design-system2/components/forward-ref';
import { Box } from '@ironhack/design-system2/components/box';
import {
  Link as DsLink,
  type LinkProps,
} from '@ironhack/design-system2/components/link';
import { type As, type ComponentWithAs } from '@ironhack/design-system2/types';
import NextLink from 'next/link';
import { useRouter } from 'next/navigation';
import { omit } from 'ramda';
import { type MouseEvent, type ReactElement, type ReactNode } from 'react';

import {
  type BasePageParams,
  getPageUrl,
  getUrlRegion,
} from '@/app/urls/url-mapping';
import config from '@/config';
import featureToggles from '@/config/feature-toggles';
import { usePageContext } from '@/hooks';
import { sendEvent } from '@/lib/gtm';
import type { DatoRoute, DatoTargetBlock } from '@/lib/datocms/types';
import type { WithTracking } from '@/types';

type Params = Pick<BasePageParams, 'language' | 'region'>;

type Props = WithTracking<DatoTargetBlock> & {
  withoutDecoration?: boolean;
  children: ReactNode;
} & LinkProps;

const getArticleHref = (previewUrl: string): string => {
  const params = previewUrl.match(
    /\/(?<region>[a-z]{2})\/(?<language>[a-z]{2})\/blog\/(?<slug>.*)/u
  )?.groups;
  if (!params) return previewUrl;

  return (
    getPageUrl('blogArticle', params as Params & { slug: string }) ?? previewUrl
  );
};

const getPageHref = (route: DatoRoute): string =>
  featureToggles['dato-route-updated-paths']
    ? route.updatedPath || route.path
    : route.path;

// To avoid updating every single URL on Dato to URL regions, this function
// converts the old region/language URLs to URL regions.
export const updateUrlRegion = (path: string): string => {
  const url = new URL(path, 'https://example.com');
  const regionLanguageRegex =
    // eslint-disable-next-line security/detect-unsafe-regex -- Dangerous REGEX? Unclear how to fix.
    /^\/(?<region>br|de|es|fr|mx|nl|pt|uk|us|ww)\/(?<language>de|en|es|fr|nl|pt)(?<rest>(\/.+)*)$/u;
  const groups = url.pathname.match(regionLanguageRegex)?.groups;
  if (!groups) return path;

  const { rest, ...params } = groups as Params & { rest: string };
  const urlRegion = getUrlRegion(params);
  url.pathname = `/${urlRegion}${rest}`;
  return url.href.replace('https://example.com', '');
};

export const getHref = (
  target: DatoTargetBlock | undefined,
  options?: { withOrigin: boolean }
): string => {
  const { withOrigin } = { withOrigin: false, ...options };

  let href = withOrigin ? '' : '#/';
  if (!target) return href;

  const { link, blogArticle, route } = target;
  if (link) href = link;
  if (blogArticle?.previewUrl) href = getArticleHref(blogArticle.previewUrl);
  if (route) href = getPageHref(route);

  const origin = withOrigin && href.startsWith('/') ? config.baseUrl : '';
  return updateUrlRegion(`${origin}${href}`);
};

// Needs forwardRef so IronhackLogoLink can pass ref for PopoverTrigger
export const Link: ComponentWithAs<As, Props> = forwardRef(
  (props: Props, ref): ReactElement => {
    const {
      children,
      gaEvent: [gaEvent] = [],
      io: isObfuscated = false,
      onClick,
      tracking,
      withoutDecoration,
      ...linkProps
    } = omit(['article', 'blogArticle', 'link', 'route'], props);

    const href = getHref(props);
    const isExternal = href.startsWith('http');
    const variant = withoutDecoration ? 'withoutDecoration' : 'default';

    const {
      pageData: { gaPageType },
    } = usePageContext();
    const router = useRouter();

    const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
      if (tracking?.f) sessionStorage.setItem('formReferrer', tracking?.f);
      if (gaEvent) sendEvent({ ...gaEvent, pageType: gaPageType });
      if (onClick) onClick(event);
      if (isObfuscated) {
        event.preventDefault();
        if (isExternal) {
          window.open(href);
        } else {
          router.push(href);
        }
      }
    };

    const BaseLink = (baseLinkProps: LinkProps) => (
      <DsLink
        onClick={handleClick}
        ref={ref}
        variant={variant}
        {...baseLinkProps}
        {...linkProps}
      />
    );

    if (isObfuscated) return <BaseLink as={Box}>{children}</BaseLink>;
    if (isExternal)
      return (
        <BaseLink display="flex" href={href} isExternal>
          {children}
        </BaseLink>
      );
    return (
      <BaseLink as={NextLink} href={href}>
        {children}
      </BaseLink>
    );
  }
);
